import React, { useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Loading } from 'src/shared/components/ui/miscellaneous/loading'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'

interface OnboardingGuardProps {
  children: React.ReactNode
}

export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { isSignedIn, needsOnboarding } = useAuthState()
  const location = useLocation()

  return useMemo(() => {
    if (isSignedIn && needsOnboarding && !location.pathname.startsWith(paths.auth.onboarding.path)) {
      return <Navigate to={paths.auth.onboarding.getHref()} replace />
    }
    return <>{children}</>
  }, [isSignedIn, needsOnboarding, location.pathname, children])
}

/** Guard to prevent access to enterprise dashboard if the user has not completed onboarding. */
export const EnterpriseOnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { isSignedIn, needsOnboarding } = useAuthState()
  const { data: user, isPending, isError } = useMyUserData({ enabled: isSignedIn })
  const onboardingComplete = !(user?.needsEnterpriseOnboarding ?? true)

  return useMemo(() => {
    if (isPending) {
      return <Loading style="fullscreen" />
    }

    if (isSignedIn && !onboardingComplete) {
      return <Navigate to={paths.enterprise.onboarding.path} replace />
    }
    return <>{children}</>
  }, [isSignedIn, onboardingComplete, children, isPending, needsOnboarding, isError])
}

/** Guard to prevent access to enterprise onboarding page if the user has completed onboarding. */
export const EnterpriseOnboardingPageGuard = ({ children }: OnboardingGuardProps) => {
  const { isSignedIn } = useAuthState()
  const { data: user } = useMyUserData({ enabled: isSignedIn })
  const onboardingComplete = !(user?.needsEnterpriseOnboarding ?? true)

  return useMemo(() => {
    if (isSignedIn && onboardingComplete) {
      return <Navigate to={paths.enterprise.dashboard.path} replace />
    }
    return <>{children}</>
  }, [isSignedIn, children, onboardingComplete])
}
